import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupDate,
  YupString,
  YupBoolean,
  promiseModalDialog,
  isNumeric,
  toastSuccess,
  YupObject,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import { gqlGetByIdQuery, gqlAdd, gqlUpdate, gqlDelete, FormInput, gqlAccept } from '../graphql';
import '../../../../styles/tab-styles.css';
import { FormFields } from './form-fields';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { RowFieldTooltips } from 'shared-pages/components';
import { IdocWasteDestinationTypes } from 'shared-pages/shared/constants';
import { getRouteByName } from 'app/routes';
import {
  isFotokiklosi,
  isAfisCyprus,
  isMedicycle,
  requiredString,
} from 'shared-pages/common/constants';
import { WCPMaterialsGridPage } from './wcp-materials-grid/wcp-materials-grid-page';
import { Tab, Tabs } from 'react-bootstrap';
import { useFormPermissions } from './wcp-materials-grid/form-permissions';
import type { FormikHelpers } from 'formik';
import { convertFieldToInt } from 'shared-pages/common/form-helpfunc';

type FormShape = Record<string, Yup.Schema<unknown>>;

// Customize FormShape based on Customer/Company
function useCustomFormShape(base: FormShape) {
  const { t } = useTranslation();
  const reqStr = t(requiredString);

  let formShape = Yup.object(base);

  // // Use concat to overlay stricter rules on top of the existing object schema.
  // formShape = formShape.concat(
  //   Yup.object({
  //     adrNumber: Yup.string().required(reqStr),
  //     adrExpDate: YupDate.required(reqStr),
  //   }),
  // );

  // // Update a field with shape (also immutable)
  // formShape = formShape.shape({
  //   name: Yup.string().notRequired(),
  //   vehicleId: formShape.fields['vehicleId'].required('Vehicle is required'),
  // });

  // Medicycle
  if (isAfisCyprus || isMedicycle) {
    // Update a field with shape (also immutable)
    formShape = formShape.shape({
      companyVat: YupString.notRequired(),
      companyTaxOffice: YupString.notRequired(),
    });
  }

  return formShape;
}

/**
 *   Form Page
 */
export const WRecPointFormPage = () => {
  const { handleGraphqlError } = useHandleError('wrecpointform');
  const { appStore } = useRootStore();
  const history = useHistory();
  const location = useLocation<{ pathname?: string }>();
  const { id } = useParams<{ id: string }>();
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const { t } = useTranslation();
  const isDA = isNew ? Boolean(location.state?.pathname?.includes('wrecpointsda')) : false;

  const newRecord = useMemo<FormValuesType>(
    () =>
      ({
        id: -1,
        isActive: true,
        wasteDestinationTypeId: isDA
          ? IdocWasteDestinationTypes.SecondaryStorage
          : IdocWasteDestinationTypes.Recycling,
      } as FormValuesType),
    [isDA],
  );

  const [addRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const [accept] = useMutation(gqlAccept);
  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetByIdQuery);
  const data = isNew ? newRecord : rawData ? rawData[Object.keys(rawData)[0]] : undefined;

  const tabStyle = { minHeight: '360px' };

  /* fetch data */
  useEffect(() => {
    get({ variables: { id: idNum } });
  }, [get, id, idNum]);

  /* user Permissions */
  const { canView, canViewReasoning, canEdit, canEditReasoning } = useFormPermissions(data);

  const formShape = {
    id: YupNumeric,
    collectorId: YupNumeric,
    legacyAppId: YupString,
    code: YupString,
    name: YupString.required(requiredString),
    wasteDestinationTypeId: YupNumeric,
    companyVat: YupString.required(requiredString),
    companyTaxOffice: YupString.required(requiredString),
    imaCode: YupString,
    contractNo: YupString,
    contractSignDate: YupDate,
    contractExpDate: YupDate,
    aepoNumber: YupString,
    aepoExpDate: YupDate,
    hazardousWastePermit: YupString,
    operationNumber: YupString,
    insurancePolicyNumber: YupString,
    insuranceExpirationDate: YupDate,
    isoExpirationDate: YupDate,
    nextIsoAuditDate: YupDate,
    aepoFileId: YupNumeric,
    hazardousWastePermitFileId: YupNumeric,
    operationFileId: YupNumeric,
    insuranceFileId: YupNumeric,
    isoFileId: YupNumeric,
    formalName: YupString,
    addressLine1: YupString.required(requiredString),
    addressLine2: YupString,
    addressArea: YupString,
    addressCity: YupString,
    addressNomos: YupString.nullable(),
    postalCode: YupString,
    regionId: YupNumeric,
    region: YupObject.nullable(),
    subregionId: YupNumeric,
    subregion: YupObject.nullable(),
    phone: YupString,
    mobile: YupString,
    fax: YupString,
    contactperson: YupString,
    email: YupString,
    email2: YupString,
    website: YupString,
    receptionPersonName: YupString,
    notes: YupString,
    recordDate: YupDate,
    isActive: YupBoolean,
    isApproved: YupBoolean,
    approvedBy: YupString,
    approvedDate: YupDate,
    erpId: YupString,
  };

  // const validationSchema = Yup.object().shape(formShape);
  const validationSchema = useCustomFormShape(formShape);
  const pickProperties = (values: FormValuesType | undefined) =>
    Lo.pick(values, Lo.keys(formShape));
  type FormValuesType = Yup.InferType<typeof validationSchema>;

  // Handler να πάει στην πίσω σελίδα
  const handleReturn = useCallback(() => {
    history.push(
      generatePath(
        getRouteByName(
          data?.wasteDestinationTypeId === IdocWasteDestinationTypes.Recycling
            ? 'WasteReceptionPoints'
            : 'WasteReceptionPointsDa',
        ),
      ),
      { ...location.state },
    );
  }, [data, history, location]);

  /**
   * * APPROVE
   */
  const handleApprove = async () => {
    accept({ variables: { id: idNum } })
      .then((d) => {
        toastSuccess(`${t('Η εγγραφή εγκρίθηκε!')} [${d.data.acceptWastereceptionpoint}]`);
        refetch();
      })
      .catch((error) => {
        handleGraphqlError('mutation-accept', error);
      });
  };

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const handleSubmit = (values: FormValuesType, actions: FormikHelpers<FormValuesType>) => {
    actions.setSubmitting(false);
    const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
    convertFieldToInt(mutValues, ['regionId', 'subregionId']);

    // Αν είναι update εγγραφής τοτε γράφω μονο τα αλλαγμένα πεδία
    console.log('[Applog] Update:', mutValues);
    if (isNew) {
      console.log('[User] New:', values);
      addRecord({ variables: { data: { ...values } } })
        .then((d) => {
          toastSuccess(`${t('Επιτυχής Προσθήκη Εγγραφής')} [${d.data[Object.keys(d.data)[0]].id}]`);
          handleReturn();
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
        });
    } else {
      updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          toastSuccess(`Επιτυχής Ενημέρωση Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        });
    }
  };

  /**
   * *  DELETE
   */
  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: 'Διαγραφή Εγγραφής',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε την Εγγραφή;',
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.goBack())
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRecord, idNum]);

  const defaultTab = () => {
    if (isFotokiklosi) return 'materials';
    else return 'materials';
  };
  const [tabKey, setTabKey] = useState(defaultTab());

  /**
   * * RENDERING
   */
  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else if (canView === false) {
    return <h3 className="text-center">{canViewReasoning}</h3>;
  } else {
    return (
      <>
        <FormCard
          title={`${
            data?.wasteDestinationTypeId === IdocWasteDestinationTypes.Recycling
              ? t('Ανακυκλωτής')
              : t('Δευτερογενή Αποθήκευση')
          }: ${isNew ? t('Νέα Εγγραφή') : data?.name}`}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          onReturn={handleReturn}
          permitSubmit={false}
          permitDelete={!isNew && appStore.isPlatformOwner}
          initialValues={pickProperties(data)}
          validationSchema={validationSchema}
          enableReinitialize
          idx={idNum}
        >
          <FormFields
            isNew={isNew}
            canEdit={canEdit}
            updateRecord={updateRecord}
            refetch={refetch}
            data={data}
            handleApprove={handleApprove}
            validationSchema={validationSchema}
          />
          {appStore.isPlatformOwner && <RowFieldTooltips data={data} />}
        </FormCard>

        {data && data.id > 0 && (
          <div>
            <Tabs activeKey={tabKey} onSelect={(k) => setTabKey(k)}>
              {isFotokiklosi && (
                <Tab eventKey="materials" title={t('Απόβλητα')} style={tabStyle}>
                  {tabKey == 'materials' && (
                    <WCPMaterialsGridPage
                      wastereceptionpointId={data.id}
                      isGridInForm={true}
                      canEdit={canEdit}
                    />
                  )}
                </Tab>
              )}
            </Tabs>
          </div>
        )}
      </>
    );
  }
};
